import { HTMLProps, ReactNode, Fragment, useState } from "react";
import cn from "classnames";
import Highlighter from "react-highlight-words";
import * as Sentry from "@sentry/react";

import { Box, Stack, Typography, Divider, Button } from "@mui/material";
import { orderBy, uniq } from "lodash";
import { useQuery } from "@tanstack/react-query";

import { formatDate, formatDateTime } from "./utils";

import trimHighlightContext from "./trimHighlightContext";

import LinkButton from "./LinkButton";
import ResolveButton from "./ResolveButton";
import CommentButton from "./CommentButton";

import styles from "./IssueDetail.module.css";
import { useTrueBizApi } from "../../../api";

import {
  APIMonitoringAnomaly,
  APIAnomalyActivityType,
  APIAnomalyDetectionSnippetType,
  APIAnomalyStatus,
  APIAnomalyDetection,
  APIMonitoringAnomalyType,
  APIAnomalyDetectionTextSnippet,
} from "../../../types/APIAnomaly";
import APIPerson from "../../../types/APIPerson";
import KeyPeople from "../../BusinessAttributes/KeyPeople";

const Highlight = ({
  children,
}: {
  children: ReactNode;
  highlightIndex: number;
}) => <strong className={styles["highlighted-text"]}>{children}</strong>;

export interface Props extends Exclude<HTMLProps<HTMLDivElement>, "children"> {
  issue: APIMonitoringAnomaly;
  suspendLoading?: boolean;
  focused?: boolean;
}

function TextSnippet({
  snippet,
  issueCreatedAt,
}: {
  snippet: APIAnomalyDetectionTextSnippet;
  issueCreatedAt: ISODateTime;
}) {
  return (
    <li className={styles.citation}>
      {snippet.direct_links.map((snippetLink, i, snippetLinks) => {
        return (
          <Fragment key={snippetLink.id}>
            <a
              href={snippetLink.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {snippetLink.display || snippetLink.href}
            </a>
            {i !== snippetLinks.length - 1 && <br />}
          </Fragment>
        );
      })}
      {snippet.highlights.length === 0 && (
        <div className={styles.context}>
          <div className={styles.soloContext}>{snippet.context}</div>
        </div>
      )}
      {snippet.highlights.map((snippetHighlight) => {
        return (
          <div key={snippetHighlight.id} className={styles.context}>
            <div className={styles.quote}>
              <Highlighter
                searchWords={[snippetHighlight.preview]}
                textToHighlight={trimHighlightContext(snippet.context, [
                  snippetHighlight.preview,
                ])}
                highlightTag={Highlight}
                autoEscape
              />
            </div>
            <div className={styles.firstSeen}>
              {/* TODO: actually track this on the backend */}
              First seen {formatDate(issueCreatedAt)}
            </div>
          </div>
        );
      })}
    </li>
  );
}

function Detection({
  issue,
  detection,
}: {
  issue: APIMonitoringAnomaly;
  detection: APIAnomalyDetection;
}) {
  const [showMore, setShowMore] = useState(false);

  const hasLinks = detection.links.length > 0;
  const hasSnippets = detection.snippets.length > 0;

  if (!hasLinks && !hasSnippets) {
    Sentry.captureMessage("detection has no links or snippets", {
      level: "error",
    });
  }

  const alertTypeString = uniq(
    orderBy(detection.alerts.map((a) => a.display))
  ).join(", ");

  if (!alertTypeString) {
    Sentry.captureMessage("detection has no alert type", { level: "error" });
  }

  const firstDetection =
    Array.isArray(issue?.detections) && issue.detections.length > 0
      ? issue.detections[0]
      : null;
  const alertDescriptions =
    firstDetection?.alerts?.[0]?.description?.trim() ?? null;

  const textSnippets = detection.snippets
    .filter((snippet) => snippet.type === APIAnomalyDetectionSnippetType.TEXT)
    .filter(
      (snippet) => snippet.context !== alertDescriptions
    ) as APIAnomalyDetectionTextSnippet[];

  if (issue.type === APIMonitoringAnomalyType.PEOPLE) {
    return (
      <Box key={detection.id}>
        {alertTypeString && (
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            {alertTypeString}
          </Typography>
        )}
        <Box pr={2} mb={2}>
          <KeyPeople
            people={detection.snippets as APIPerson[]}
            withTitle={false}
          />
        </Box>
      </Box>
    );
  }

  return (
    <div>
      <div className={styles.type}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
          {uniq(orderBy(detection.alerts.map((a) => a.display))).join(", ") ||
            "unknown"}
        </Typography>
      </div>

      {/* Show snippets and links */}
      {alertDescriptions && (
        <Typography mb={2} fontStyle="italic">
          {alertDescriptions}
        </Typography>
      )}

      {textSnippets.length > 0 && (
        <Stack>
          <ul
            style={{
              listStyleType: "none",
              marginTop: ".25em",
              paddingLeft: "1em",
              paddingRight: "2.75em",
              wordBreak: "break-word",
            }}
          >
            {textSnippets.slice(0, showMore ? undefined : 3).map((snippet) => (
              <TextSnippet
                snippet={snippet}
                issueCreatedAt={issue.created_at}
                key={snippet.id}
              />
            ))}
          </ul>
          {textSnippets.length > 3 && (
            <Box>
              <Button onClick={() => setShowMore(!showMore)}>
                {showMore
                  ? "Show Less"
                  : `Show ${textSnippets.length - 3} More`}
              </Button>
            </Box>
          )}
        </Stack>
      )}

      {hasLinks && !hasSnippets && (
        <Stack>
          <div>Related content was found on the following pages:</div>
          <ul
            style={{
              paddingLeft: "1em",
              paddingRight: "2.75em",
              wordBreak: "break-word",
            }}
          >
            {detection.links.map((link) => (
              <li key={link.id} className={styles.citation}>
                <a href={link.href} target="_blank" rel="noopener noreferrer">
                  {link.display || link.href}
                </a>
              </li>
            ))}
          </ul>
        </Stack>
      )}
    </div>
  );
}

export default function IssueDetail({
  issue,
  suspendLoading = false,
  focused = false,
  className,
  ...etc
}: Props) {
  const api = useTrueBizApi();

  const allPeopleQuery = useQuery({
    queryKey: ["allPeople"],
    queryFn: () => api.getAnomaliesMetaAssignableUsers(),
  });

  const comments = issue.activity.filter(
    (activity) => activity.type === APIAnomalyActivityType.COMMENTED
  );

  return (
    <div
      {...etc}
      className={cn(styles.issue, className, {
        [styles.unresolved]: issue.status !== APIAnomalyStatus.RESOLVED,
        [styles.focused]: focused,
      })}
    >
      <div className={styles.badge}>
        <div className={styles.date}>{formatDateTime(issue.created_at)}</div>
        {/* <div className={styles.status}>{formatStatus(issue.status)}</div> */}
      </div>
      <div className={styles.controls}>
        <div className={styles.actionButtons}>
          <LinkButton title="Copy link" issueId={issue.id} />
        </div>
      </div>

      <div className={styles.details}>
        <div className={styles.summary}>
          {issue.resolution && (
            <Box>
              <Box sx={{ mb: 0.5 }}>
                Resolved by:{" "}
                <strong>
                  {issue.resolution.resolved_by.email || "Unknown"}
                </strong>
              </Box>
              <Box sx={{ mb: 0.5 }}>
                Resolved at:{" "}
                <strong>{formatDateTime(issue.resolution.created_at)}</strong>
              </Box>
              <Box>
                Resolution note: <strong>{issue.resolution.description}</strong>
              </Box>
              <Divider sx={{ mt: 1, mb: 2 }} />
            </Box>
          )}

          {issue.detections.map((detection) => (
            <Detection key={detection.id} issue={issue} detection={detection} />
          ))}

          <Box display="flex" justifyContent="right" gap={2} mt={2}>
            <CommentButton
              issue={issue}
              monitorDomain={issue.monitors[0]?.domain}
              allPeopleQuery={allPeopleQuery}
            />
            <ResolveButton
              title={
                issue.status === APIAnomalyStatus.RESOLVED
                  ? "This issue has already been resolved"
                  : "Resolve issue"
              }
              issueId={issue.id}
              monitorDomain={issue.monitors[0]?.domain}
              disabled={issue.status === APIAnomalyStatus.RESOLVED}
            />
          </Box>
        </div>
        {comments.length > 0 && (
          <Box>
            {comments.map((comment) => (
              <Stack key={comment.id} mt={2}>
                <Stack
                  direction="row"
                  gap="1em"
                  alignItems="baseline"
                  justifyContent="space-between"
                >
                  <Typography variant="subtitle2">
                    <strong>
                      {comment.comment.commenter.email || "A TrueBizUser"}
                    </strong>{" "}
                    commented:
                  </Typography>
                  <Typography className={styles.timestamp} variant="subtitle2">
                    {comment.id === "PENDING"
                      ? "Pending...."
                      : formatDateTime(comment.created_at)}
                  </Typography>
                </Stack>
                <div
                  className={cn(styles.commentBody, {
                    [styles.pending]: comment.id === "PENDING",
                  })}
                >
                  {comment.comment.message}
                </div>
              </Stack>
            ))}
          </Box>
        )}
      </div>
    </div>
  );
}
